import React from 'react';
import { CheckCircle, GitFork, Tag, FunctionSquare, Zap } from 'lucide-react';
import MappingProgressBar, { MappingProgressProps } from './MappingProgressBar'; // Import MappingProgressBar and its props

interface PlaybookStatsPanelProps {
  playbook: {
    playbookId: string;
    name: string;
    description: string;
    type: string;
    version: string;
    params: { name: string; exampleValue?: string }[];
    input_schema_id?: string; // Changed from inputSchemaId
    output_schema_id?: string; // Changed from outputSchemaId
    uiTransforms: any[];
    uiEventConfigs: any[];
    autoPromoteGlobals: boolean;
    isNewPlaybook: boolean;
    isSubmitting: boolean;
    baselineSchema?: 'input' | 'output'; // Add baselineSchema
  };
  updateState?: (updates: any) => void;
  mappingProgress: MappingProgressProps; // Add this prop
}

const PlaybookStatsPanel: React.FC<PlaybookStatsPanelProps> = ({ playbook, mappingProgress }) => {
  
  const transformCount = playbook.uiTransforms.length;
  const eventCount = playbook.uiEventConfigs.length;
  
  return (
    <div className="space-y-6">
      {/* General Statistics */}
      <div className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Playbook Statistics
        </h3>
        
        <div className="space-y-4">
          {/* Creation Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              playbook.isNewPlaybook 
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            }`}>
              {playbook.isNewPlaybook ? 'New' : 'Saved'}
            </span>
          </div>

          {/* Type */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <GitFork className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Type</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
              {playbook.type}
            </span>
          </div>

          {/* Version */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Tag className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Version</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {playbook.version}
            </span>
          </div>

          {/* Transform Count */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FunctionSquare className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Transforms</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {transformCount}
            </span>
          </div>

          {/* Event Count */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Zap className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Events</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {eventCount}
            </span>
          </div>
        </div>
      </div>

      {/* Mapping Progress */}
      <div className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Mapping Progress
        </h3>
        <MappingProgressBar 
          mappedCount={mappingProgress.mappedCount}
          totalCount={mappingProgress.totalCount}
          requiredMappedCount={mappingProgress.requiredMappedCount}
          requiredTotalCount={mappingProgress.requiredTotalCount}
          missingCount={mappingProgress.missingCount || 0}
          extraCount={mappingProgress.extraCount || 0}
          mismatchCount={mappingProgress.mismatchCount || 0}
          baselineSchema={mappingProgress.baselineSchema}
        />
      </div>
    </div>
  );
};

export default PlaybookStatsPanel;
